import * as THREE from "three";
import type { AudioHook } from "@spacecrew/shared";

/**
 * Fixtures: the things in the station you can actually reach out and operate — toggles, push
 * buttons, levers, rotary dials, drawers, valve wheels.
 *
 * Design rule (see DECISIONS.md, 2026-08-24): a fixture is a *tactile* path to something the
 * terminal can already do, never the only path. Unbound fixtures are scenery that moves and
 * clicks and remembers its own position, and they never touch simulation state. When a task or
 * puzzle claims a fixture it supplies `onActivate`, which sends a normal server intent — the
 * server stays authoritative and the DOM control for the same action keeps working, so nothing
 * here is load-bearing for keyboard or screen-reader players.
 */

export type FixtureKind = "switch" | "button" | "lever" | "dial" | "drawer" | "wheel";

export interface FixtureSpec {
  /** Stable id. Tasks bind by this, so treat it as a name, not a label: `reactor.pump.1`. */
  id: string;
  /** In-fiction caption shown while the cursor is on it. Worn label-maker tape, not UI copy. */
  label: string;
  kind: FixtureKind;
  /** The part that moves. Its transform is driven from the fixture's value. */
  pivot: THREE.Object3D;
  /** Pick volume — usually a mesh in the pivot, sometimes an oversized invisible box. */
  hit: THREE.Object3D;
  /** Travel: radians for switch/lever/dial/wheel, metres for drawer. */
  travel: number;
  /** Rotary fixtures stop at N detents; everything else is two-state. */
  detents?: number;
  /** Bolted shut: it takes the input, refuses, and says so with a dull thud. */
  stuck?: boolean;
  /** An emissive pilot lamp that reports the fixture's position back to the room. */
  lamp?: THREE.Mesh;
  lampOn?: number;
  lampOff?: number;
  /** Bound fixtures report the new value to the game. Absent = scenery. */
  onActivate?: (value: number) => void;
}

const AUDIO: Record<FixtureKind, AudioHook> = {
  switch: "fixture.switch", button: "fixture.button", lever: "fixture.lever",
  dial: "fixture.dial", drawer: "fixture.drawer", wheel: "fixture.dial",
};
/** Seconds of travel. A button is a snap; a drawer is a slide you can hear the length of. */
const DURATION: Record<FixtureKind, number> = {
  switch: 0.09, button: 0.05, lever: 0.22, dial: 0.11, drawer: 0.34, wheel: 0.3,
};

interface Live {
  spec: FixtureSpec;
  value: number;    // where the fixture actually is, in detents (0..detents-1) or 0|1
  shown: number;    // what the mesh is currently showing — eased toward `value`
  home: { rx: number; ry: number; rz: number; pz: number };
  release?: number; // momentary fixtures spring back at this timestamp
}

const ease = (t: number) => 1 - (1 - t) ** 3;

export class Fixtures {
  private live: Live[] = [];
  private byHit = new Map<THREE.Object3D, Live>();
  private ray = new THREE.Raycaster();
  private hovered: Live | null = null;
  private baseTints = new Map<THREE.Object3D, THREE.Color>();
  private hits: THREE.Object3D[] = [];

  add(spec: FixtureSpec): FixtureSpec {
    const l: Live = {
      spec, value: 0, shown: 0,
      home: { rx: spec.pivot.rotation.x, ry: spec.pivot.rotation.y, rz: spec.pivot.rotation.z, pz: spec.pivot.position.z },
    };
    this.live.push(l);
    this.byHit.set(spec.hit, l);
    this.hits.push(spec.hit);
    // Remember every tint under the pivot so hover can lift the whole fixture, not one face.
    spec.pivot.traverse((o) => {
      const u = (o as THREE.Mesh).material as THREE.ShaderMaterial | undefined;
      if (u?.uniforms?.uTint) this.baseTints.set(o, u.uniforms.uTint.value.clone());
    });
    return spec;
  }

  /** What is under the pointer, in normalised device coordinates. */
  pick(ndc: THREE.Vector2, camera: THREE.Camera): FixtureSpec | null {
    this.ray.setFromCamera(ndc, camera);
    const hit = this.ray.intersectObjects(this.hits, true)[0];
    if (!hit) return null;
    // The pick volume may be a child of the registered object.
    for (let o: THREE.Object3D | null = hit.object; o; o = o.parent) {
      const l = this.byHit.get(o);
      if (l) return l.spec;
    }
    return null;
  }

  setHover(spec: FixtureSpec | null): void {
    const next = spec ? this.live.find((l) => l.spec === spec) ?? null : null;
    if (next === this.hovered) return;
    for (const l of [this.hovered, next]) {
      if (!l) continue;
      const lift = l === next ? 1.55 : 1;
      l.spec.pivot.traverse((o) => {
        const base = this.baseTints.get(o);
        const u = (o as THREE.Mesh).material as THREE.ShaderMaterial | undefined;
        if (base && u?.uniforms?.uTint) u.uniforms.uTint.value.copy(base).multiplyScalar(lift);
      });
    }
    this.hovered = next;
  }

  /** Operate a fixture. Returns the audio hook to play, and the new value for bound fixtures. */
  activate(spec: FixtureSpec, now: number): { hook: AudioHook; value: number } {
    const l = this.live.find((x) => x.spec === spec)!;
    if (spec.stuck) return { hook: "fixture.stuck", value: l.value };
    if (spec.kind === "button") {
      l.value = 1;
      l.release = now + 0.09;
    } else if (spec.detents && spec.detents > 2) {
      l.value = (l.value + 1) % spec.detents;
    } else {
      l.value = l.value ? 0 : 1;
    }
    spec.onActivate?.(l.value);
    return { hook: AUDIO[spec.kind], value: l.value };
  }

  valueOf(spec: FixtureSpec): number { return this.live.find((x) => x.spec === spec)?.value ?? 0; }

  update(dt: number, now: number): void {
    for (const l of this.live) {
      if (l.release !== undefined && now >= l.release) { l.value = 0; l.release = undefined; }
      const step = dt / DURATION[l.spec.kind];
      if (Math.abs(l.shown - l.value) > 0.001) {
        const dir = Math.sign(l.value - l.shown);
        const raw = Math.min(1, Math.abs(l.value - l.shown));
        l.shown += dir * Math.min(Math.abs(l.value - l.shown), ease(Math.min(1, step)) * Math.max(raw, 0.25));
      } else l.shown = l.value;
      if (l.spec.lamp) {
        const u = (l.spec.lamp.material as THREE.ShaderMaterial).uniforms.uTint;
        if (u) u.value.set(l.value ? l.spec.lampOn ?? 0x39ff8a : l.spec.lampOff ?? 0x2a0f0c);
      }
      const { pivot } = l.spec, { home } = l, s = l.shown;
      switch (l.spec.kind) {
        case "drawer": pivot.position.z = home.pz + l.spec.travel * s; break;
        case "dial": case "wheel": pivot.rotation.z = home.rz + l.spec.travel * s; break;
        default: pivot.rotation.x = home.rx + l.spec.travel * s; break;
      }
    }
  }
}
